export interface ImageBuild {
  id: string;
  name: string;
  inputImage: string;
  outputImage: string;
  status: 'complete' | 'pushing' | 'failed' | 'building' | 'queued';
  binding: 'early' | 'late';
  created: string;
  // Additional details for expandable row
  description?: string;
  buildName?: string; // Build name (UI label from create form)
  buildDuration?: string;
  exportFormats?: string[];
  registryUrl?: string;
  errorMessage?: string;
  progress?: number;
  uuid?: string;
  architecture?: string;
  sharedWith?: string;
  ami?: string;
  region?: string;
  // Base image parameters
  sourceImage?: string; // The selected source image name (e.g., "RHEL 9.4 - x86_64")
  baseImageName?: string; // The image name field
  baseImageTag?: string; // The tag field
  baseImageRegistry?: string; // The registry URL
  // Image output parameters
  outputRegistry?: string; // The target registry URL or name
  outputImageName?: string; // The output image name field
  outputTag?: string; // The output tag field
  autoGenerateTag?: boolean; // Whether tag is auto-generated
  includeFlightControlAgent?: boolean; // Whether flight-control-agent is included
  // Registration parameters
  autoCreateCert?: boolean; // Whether to auto-create certificate
  registrationWindow?: string; // Registration window (e.g., "360 days" or custom date)
  remoteAccess?: string; // e.g. "SSH public key" or "Not configured"
}

// Helper function to get date 29 minutes ago
const getDate29MinutesAgo = (): string => {
  const date = new Date(Date.now() - 29 * 60 * 1000);
  return date.toISOString();
};

// Helper function to get date 3 hours ago
const getDate3HoursAgo = (): string => {
  const date = new Date(Date.now() - 3 * 60 * 60 * 1000);
  return date.toISOString();
};

// Helper function to get date X minutes ago
const getDateMinutesAgo = (minutes: number): string => {
  const date = new Date(Date.now() - minutes * 60 * 1000);
  return date.toISOString();
};

// Helper function to get date X hours ago
const getDateHoursAgo = (hours: number): string => {
  const date = new Date(Date.now() - hours * 60 * 60 * 1000);
  return date.toISOString();
};

// Helper function to get date for today at specific time
const getDateTodayAt = (hours: number, minutes: number): string => {
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
};

// Helper function to get date for yesterday at specific time
const getDateYesterdayAt = (hours: number, minutes: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
};

// Helper function to get date for specific date this year
const getDateThisYear = (month: number, day: number, hours: number, minutes: number): string => {
  const date = new Date();
  date.setMonth(month - 1, day);
  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
};

// Helper function to get date for previous year
const getDatePreviousYear = (year: number, month: number, day: number): string => {
  const date = new Date(year, month - 1, day);
  return date.toISOString();
};

export const mockImageBuilds: ImageBuild[] = [
  {
    id: 'build-5',
    name: 'Hello Buggy',
    inputImage: 'rhel9/rhel-bootc:9.5',
    outputImage: 'my-edge-img:v1.0.0',
    status: 'queued',
    binding: 'early',
    created: getDateYesterdayAt(14, 15), // Yesterday, 2:15 PM
    description: 'Production edge image build for v1.0.0 release',
    buildName: 'Hello Buggy',
    buildDuration: 'Pending',
    exportFormats: ['iso', 'qcow2-container', 'vmdk', 'qcow2'],
    registryUrl: 'quay.io/myorg/my-edge-img:v1.0.0',
    uuid: 'd4e8f2a6-3c7b-4d9f-8e1c-5f3a7b9d2e4c',
    architecture: 'x86_64',
    sharedWith: '1234567',
    // Base image parameters
    sourceImage: 'RHEL 9.5',
    baseImageName: 'rhel9/rhel-bootc',
    baseImageTag: '9.5',
    baseImageRegistry: 'registry.redhat.io',
    // Image output parameters
    outputRegistry: 'external.example',
    outputImageName: 'my-edge-img',
    outputTag: 'v1.0.0',
    autoGenerateTag: false,
    includeFlightControlAgent: true,
    // Registration parameters
    autoCreateCert: true,
    registrationWindow: '360 days',
    remoteAccess: 'SSH public key',
  },
  {
    id: 'build-2',
    name: 'Hello Kitty',
    inputImage: 'rhel10/rhel-bootc:10.0',
    outputImage: 'my-edge-img:v2.1.0',
    status: 'pushing',
    binding: 'late',
    created: getDateMinutesAgo(2), // 2 minutes ago
    description: 'Production edge image build for v2.1.0 release',
    buildName: 'Hello Kitty',
    buildDuration: '45 minutes',
    exportFormats: ['vmdk', 'qcow2'],
    registryUrl: 'quay.io/myorg/my-edge-img:v2.1.0',
    progress: 75,
    uuid: 'a1b2c3d4-5e6f-7g8h-9i0j-k1l2m3n4o5p6',
    architecture: 'x86_64',
    sharedWith: '2345678',
    // Base image parameters
    sourceImage: 'RHEL 10',
    baseImageName: 'rhel10/rhel-bootc',
    baseImageTag: '10.0',
    baseImageRegistry: 'registry.redhat.io',
    // Image output parameters
    outputRegistry: 'external.example',
    outputImageName: 'my-edge-img',
    outputTag: 'v2.1.0',
    autoGenerateTag: false,
    includeFlightControlAgent: true,
    // Registration parameters (late binding - no cert needed)
    remoteAccess: 'Not configured',
  },
  {
    id: 'build-1',
    name: 'Pixel Panda',
    inputImage: 'rhel9/rhel-bootc:9.5',
    outputImage: 'my-edge-img:v1.0.0',
    status: 'complete',
    binding: 'early',
    created: getDatePreviousYear(2024, 12, 20), // Dec 20, 2024
    description: 'Production edge image build for v1.0.0 release',
    buildName: 'Pixel Panda',
    buildDuration: '1 hour 15 minutes',
    exportFormats: ['iso', 'qcow2-container', 'vmdk', 'qcow2'],
    registryUrl: 'quay.io/myorg/my-edge-img:v1.0.0',
    uuid: 'b5c6d7e8-9f0a-1b2c-3d4e-5f6a7b8c9d0e',
    architecture: 'x86_64',
    sharedWith: '3456789',
    ami: 'ami-0d52c8df27df8a728',
    region: 'us-east-1',
    // Base image parameters
    sourceImage: 'RHEL 9.5',
    baseImageName: 'rhel9/rhel-bootc',
    baseImageTag: '9.5',
    baseImageRegistry: 'registry.redhat.io',
    // Image output parameters
    outputRegistry: 'external.example',
    outputImageName: 'my-edge-img',
    outputTag: 'v1.0.0',
    autoGenerateTag: false,
    includeFlightControlAgent: true,
    // Registration parameters
    autoCreateCert: true,
    registrationWindow: '360 days',
    remoteAccess: 'SSH public key',
  },
  {
    id: 'build-3',
    name: 'Cosmic Noodle',
    inputImage: 'rhel9/rhel-bootc:9.3',
    outputImage: 'my-edge-img:v1.5.0',
    status: 'failed',
    binding: 'late',
    created: getDateThisYear(1, 12, 9, 30), // Jan 12, 9:30 AM
    description: 'Production edge image build for v1.5.0 release',
    buildName: 'Cosmic Noodle',
    buildDuration: 'Failed after 30 minutes',
    exportFormats: ['vmdk', 'qcow2'],
    registryUrl: 'quay.io/myorg/my-edge-img:v1.5.0',
    errorMessage: 'Build failed due to insufficient disk space. Please free up space and retry.',
    uuid: 'c6d7e8f9-0a1b-2c3d-4e5f-6a7b8c9d0e1f',
    architecture: 'x86_64',
    // Base image parameters
    sourceImage: 'RHEL 9.3',
    baseImageName: 'rhel9/rhel-bootc',
    baseImageTag: '9.3',
    baseImageRegistry: 'registry.redhat.io',
    // Image output parameters
    outputRegistry: 'external.example',
    outputImageName: 'my-edge-img',
    outputTag: 'v1.5.0',
    autoGenerateTag: false,
    includeFlightControlAgent: true,
    // Registration parameters (late binding - no cert needed)
    remoteAccess: 'Not configured',
  },
  {
    id: 'build-4',
    name: 'Captain Compile',
    inputImage: 'rhel10/rhel-bootc:10.0',
    outputImage: 'my-edge-img:v3.0.0',
    status: 'building',
    binding: 'early',
    created: getDateTodayAt(10, 45), // Today, 10:45 AM
    description: 'Production edge image build for v3.0.0 release',
    buildName: 'Captain Compile',
    buildDuration: 'In progress',
    exportFormats: ['iso', 'qcow2-container', 'vmdk', 'qcow2'],
    registryUrl: 'quay.io/myorg/my-edge-img:v3.0.0',
    progress: 45,
    uuid: 'd7e8f9a0-1b2c-3d4e-5f6a-7b8c9d0e1f2a',
    architecture: 'x86_64',
    sharedWith: '4567890',
    // Base image parameters
    sourceImage: 'RHEL 10',
    baseImageName: 'rhel10/rhel-bootc',
    baseImageTag: '10.0',
    baseImageRegistry: 'registry.redhat.io',
    // Image output parameters
    outputRegistry: 'external.example',
    outputImageName: 'my-edge-img',
    outputTag: 'v3.0.0',
    autoGenerateTag: false,
    includeFlightControlAgent: true,
    // Registration parameters
    autoCreateCert: true,
    registrationWindow: '360 days',
    remoteAccess: 'Not configured',
  },
  {
    id: 'build-6',
    name: 'Glitchy Kitty',
    inputImage: 'rhel8/rhel-bootc:8.10',
    outputImage: 'my-edge-img:v2.0.0',
    status: 'complete',
    binding: 'early',
    created: getDatePreviousYear(2024, 10, 15), // Oct 15, 2024 – last row (oldest)
    description: 'Production edge image build for v2.0.0 release',
    buildName: 'Glitchy Kitty',
    buildDuration: '1 hour 5 minutes',
    exportFormats: ['iso', 'qcow2-container', 'vmdk', 'qcow2'],
    registryUrl: 'quay.io/myorg/my-edge-img:v2.0.0',
    uuid: 'e8f9a0b1-2c3d-4e5f-6a7b-8c9d0e1f2a3b',
    architecture: 'x86_64',
    sharedWith: '5678901',
    // Base image parameters
    sourceImage: 'RHEL 8.10',
    baseImageName: 'rhel8/rhel-bootc',
    baseImageTag: '8.10',
    baseImageRegistry: 'registry.redhat.io',
    // Image output parameters
    outputRegistry: 'external.example',
    outputImageName: 'my-edge-img',
    outputTag: 'v2.0.0',
    autoGenerateTag: false,
    includeFlightControlAgent: true,
    // Registration parameters
    autoCreateCert: true,
    registrationWindow: '360 days',
    remoteAccess: 'SSH public key',
  },
];

export const getImageBuildById = (id: string): ImageBuild | undefined => {
  return mockImageBuilds.find(build => build.id === id);
};

// ImageExport task interface
export interface ImageExport {
  id: string;
  buildId: string;
  buildName: string;
  format: 'VMDK' | 'QCOW2' | 'ISO' | 'QCOW2-CONTAINER';
  status: 'pending' | 'exporting' | 'complete' | 'failed';
  errorMessage?: string;
  logs?: string[];
  created: string;
  /** Custom filename for tooltip (e.g. on details page info icon); when set, overrides default from build + format */
  suggestedFilename?: string;
}

// Generate export tasks based on image builds
export const generateImageExports = (builds: ImageBuild[]): ImageExport[] => {
  const exports: ImageExport[] = [];
  
  builds.forEach(build => {
    // Only create exports for completed builds
    if (build.status === 'complete' && build.exportFormats) {
      const allCompleteForBuild = build.id === 'build-6'; // Last row (2024-10-15): 4 complete images
      build.exportFormats.forEach((format, index) => {
        const formatLower = format.toLowerCase();
        let formatUpper: 'VMDK' | 'QCOW2' | 'ISO' | 'QCOW2-CONTAINER';
        if (formatLower === 'qcow2-container') {
          formatUpper = 'QCOW2-CONTAINER';
        } else {
          formatUpper = format.toUpperCase() as 'VMDK' | 'QCOW2' | 'ISO';
        }
        const status = allCompleteForBuild
          ? 'complete'
          : build.id === 'build-1' && index === 0
            ? 'failed'
            : formatUpper === 'VMDK' || formatUpper === 'QCOW2' || formatUpper === 'QCOW2-CONTAINER'
              ? 'complete'
              : 'pending';
        const logs = status === 'complete'
          ? [
              'Starting export process...',
              formatUpper === 'VMDK' ? 'Converting image to VMDK format...' : formatUpper === 'QCOW2' ? 'Converting image to QCOW2 format...' : formatUpper === 'ISO' ? 'Converting image to ISO format...' : 'Converting image to QCOW2 (Container) format...',
              'Export completed successfully'
            ]
          : formatUpper === 'QCOW2'
            ? ['Starting export process...', 'Converting image to QCOW2 format...', 'Export in progress...']
            : [];
        exports.push({
          id: `export-${build.id}-${format}-${index}`,
          buildId: build.id,
          buildName: build.buildName || build.name,
          format: formatUpper,
          status,
          created: build.created,
          logs,
          errorMessage: status === 'failed' ? 'Export failed. You can retry or delete this export.' : undefined,
        });
      });
      // Pixel Panda (build-1): add 6 extra failed ISO exports for details page (10 total)
      if (build.id === 'build-1') {
        const extraFailedIsos: Array<{ created: string; suggestedFilename: string }> = [
          { created: getDatePreviousYear(2024, 12, 19), suggestedFilename: 'my-edge-img-v1-installer.iso' },
          { created: getDatePreviousYear(2024, 12, 18), suggestedFilename: 'prod-edge-v1-0-0-installer.iso' },
          { created: getDatePreviousYear(2024, 12, 15), suggestedFilename: 'rhel9-edge-installer.iso' },
          { created: getDatePreviousYear(2024, 12, 10), suggestedFilename: 'custom-image-installer.iso' },
          { created: getDatePreviousYear(2024, 11, 28), suggestedFilename: 'pixel-panda-beta-installer.iso' },
          { created: getDatePreviousYear(2024, 11, 20), suggestedFilename: 'prod-edge-v1-0-0-backup-installer.iso' },
        ];
        extraFailedIsos.forEach((item, idx) => {
          exports.push({
            id: `export-${build.id}-iso-failed-${idx}`,
            buildId: build.id,
            buildName: build.buildName || build.name,
            format: 'ISO',
            status: 'failed',
            created: item.created,
            suggestedFilename: item.suggestedFilename,
            errorMessage: 'Export failed. You can retry or delete this export.',
          });
        });
      }
    }
    // Add failed export example for failed build (build-3)
    if (build.id === 'build-3') {
      exports.push({
        id: `export-${build.id}-vmdk-failed`,
        buildId: build.id,
        buildName: build.buildName || build.name,
        format: 'VMDK',
        status: 'failed',
        errorMessage: 'Export failed due to insufficient storage space. Please free up space and retry.',
        created: build.created,
      });
    }
  });
  
  return exports;
};

export const mockImageExports: ImageExport[] = generateImageExports(mockImageBuilds);

